import React from "react";

import classnames from "classnames";
import createDecorator from "final-form-focus";
import isIntuit from "Intuit/isIntuit";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { SubBody } from "Cobalt/BodyText";
import { Modal, ModalHeader, ModalBody, ModalTitle, ModalFooter, closeModal } from "Cobalt/Modal";
import BodyText from "Common/BodyText";
import Button from "Common/Button";
import { SIFormSelect, SIFinalFormField } from "Common/forms";
import { Row, Col } from "Common/Grid";
import Icon from "Common/Icon";

import { ALTERNATE_DOCS_MODAL_NAME } from "./constants";
import { getModalName, useModalDescription, docTypeForAnalytics, useDocumentPickerContext, bem, responsiveBem, convertFileToFormData } from "./utils";

const focusOnErrors = createDecorator();

const getAltDocFileInputName = (document) => `${document.type}[${document.key ? document.key : ""}]`;

const ErrorMessage = ({ error }) => (
  <Row>
    <Col>
      <BodyText>
        <p className={responsiveBem("Failure")}>
          <Icon icon="times" flavor="solid" pr="sm" style={{ marginRight: "2px" }} />

          {error}
        </p>
      </BodyText>
    </Col>
  </Row>
);

const AlternativeItem = ({ title, description: fallbackDescription }, index) => {
  const descriptionPayload = {
    fileInputName: getAltDocFileInputName(document),
    description: fallbackDescription,
    title,
  };

  const description = useModalDescription(descriptionPayload);

  return (
    <div key={index}>
      <BodyText weight="bold" mb={3}>
        How do I get {title}?
      </BodyText>
      <SubBody className={bem("AltSubBody")} mb={index !== 0 ? 0 : 5}>
        {description}
      </SubBody>
    </div>
  );
};

const AlternateDocumentsModal = () => {
  const {
    index,
    fileInputName,
    primaryAlternatives,
    secondaryAlternatives,
    onDocumentUpload,
    onDocumentUploadSuccess,
    mainDocumentState: { uploadFile: uploadMainFile, explanationToFile },
    userFlagsState: { persistedAlternativeType, persistedExplanation, updateFlags },

    alternativeDocumentsState: { uploading, altFilenames, altErrors, setUploading, setAltFilenames, setAltErrors },
  } = useDocumentPickerContext();
  const dispatch = useDispatch();
  const currentlyOpenModal = useSelector((state) => state.openModal);
  const modalName = getModalName(ALTERNATE_DOCS_MODAL_NAME, index);

  const handleAlternativeTypeChange = (e) => {
    const { value } = e.target;
    const formattedValue = {
      primary: "yes",
      secondary: "no",
    }[value];
    if (formattedValue) {
      AnalyticsTracking.trackEvent(`${docTypeForAnalytics(fileInputName)}_${formattedValue}_selected`, {
        location: "document_picker",
        main_file_type: fileInputName,
      });
    }
  };

  const handleFileChange = async (e, altDocIndex, alternativeDocumentType) => {
    e.persist();
    setUploading(true);
    const file = e.target.files[0];
    const updatedFilenames = { ...altFilenames };
    updatedFilenames[alternativeDocumentType][altDocIndex] = file.name;
    setAltFilenames(updatedFilenames);

    const formData = convertFileToFormData(file, e.target.name, altDocIndex !== undefined);

    const response = await onDocumentUpload(formData);
    const data = await response.json();
    const newError = data?.errors?.[0]?.detail || null;
    const updatedErrors = {
      ...altErrors,
      [alternativeDocumentType]: {
        ...altErrors[alternativeDocumentType],
        [altDocIndex]: newError,
      },
    };

    setAltErrors(updatedErrors);
    setUploading(false);
    if (response.status === 200) {
      await updateFlags({ answer: alternativeDocumentType });
    }
    onDocumentUploadSuccess(data);
  };

  const alternativesList = (alternativeDocumentType) => {
    if (alternativeDocumentType === "primary") {
      return primaryAlternatives;
    }
    if (alternativeDocumentType === "secondary") {
      return secondaryAlternatives;
    }
    return [];
  };

  return (
    <Modal onHide={() => dispatch(closeModal())} show={currentlyOpenModal === modalName} aria-hidden={currentlyOpenModal !== modalName}>
      <Form
        initialValues={{
          alternativeDocumentType: persistedAlternativeType,
          explanation: persistedExplanation,
        }}
        decorators={[focusOnErrors]}
        onSubmit={async () => dispatch(closeModal())}
      >
        {({ handleSubmit, submitting, values: { alternativeDocumentType, explanation } }) => (
          <form onSubmit={handleSubmit}>
            <ModalHeader closeButton>
              <ModalTitle>Upload Alternative</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <Row>
                <Col md={5} className={bem("AlternativeDocumentTypeQuestion")}>
                  <SIFinalFormField
                    name="alternativeDocumentType"
                    data-testid="AlternativeDocumentType"
                    label={`Do you have ${primaryAlternatives[0].title}?`}
                    component={SIFormSelect}
                    onChange={handleAlternativeTypeChange}
                    componentProps={{
                      options: {
                        Yes: "primary",
                        No: "secondary",
                      },
                    }}
                    mb={isIntuit() ? 5 : 6}
                  />

                  {alternativesList(alternativeDocumentType).map((document, index) => {
                    const filename = altFilenames[alternativeDocumentType][index];
                    const error = altErrors[alternativeDocumentType][index];

                    return (
                      <div key={index}>
                        <BodyText weight="bold" mt={isIntuit() ? 4 : 5} mb={isIntuit() ? 2 : 3}>
                          {document.title}
                        </BodyText>
                        {error && <ErrorMessage error={error} />}
                        <input
                          type="file"
                          name={getAltDocFileInputName(document)}
                          className={bem("RealFilePicker")}
                          onChange={(e) => handleFileChange(e, index, alternativeDocumentType)}
                          id={`alt-input-${getAltDocFileInputName(document)}`}
                          data-testid={`alt-input-${getAltDocFileInputName(document)}`}
                        />

                        <label type="text" className={classnames(bem("Filename"))} htmlFor={`alt-input-${getAltDocFileInputName(document)}`}>
                          {filename || (
                            <>
                              <Icon icon="upload" mr={1} flavor="solid" /> Select a file...
                            </>
                          )}
                        </label>
                        {document.information && <SubBody className={bem("AltSubBody")}>{document.information}</SubBody>}
                      </div>
                    );
                  })}
                </Col>
                <Col md={7}>{alternativesList(alternativeDocumentType).map(AlternativeItem)}</Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="gray" onClick={() => dispatch(closeModal())} {...(isIntuit() ? { size: "medium" } : {})}>
                Cancel
              </Button>
              <Button color="blue" type="submit" disabled={submitting || uploading} {...(isIntuit() ? { size: "medium" } : {})}>
                {submitting || uploading ? (
                  <>
                    <Icon icon="cog" flavor="solid" spin mr={3} />
                    Loading...
                  </>
                ) : (
                  "Done"
                )}
              </Button>
            </ModalFooter>
          </form>
        )}
      </Form>
    </Modal>
  );
};

export default AlternateDocumentsModal;
